import { useEffect, useState } from 'react'
import { Platform } from 'react-native'

/**
 * Cross-platform keyboard bottom offset.
 *
 * - Web: the OS keyboard shrinks the visual viewport while leaving the
 *   layout viewport at full height, so `KeyboardAvoidingView` (which is
 *   a no-op on RN-Web) cannot push content up. We compute the gap from
 *   `window.visualViewport`.
 * - Native: returns 0. iOS uses `KeyboardAvoidingView behavior="padding"`
 *   (combined with `tabBarHideOnKeyboard` on tab screens); Android uses
 *   `KeyboardAwareView` (`behavior="height"`), since edge-to-edge (Android
 *   15/16) stopped `adjustResize` from shrinking the window. Both shrink the
 *   available area for us, so the consumer should not add extra padding on top.
 */
export const useKeyboardOffset = (): number => {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined' || !window.visualViewport) return

    const vv = window.visualViewport
    const update = () => {
      // Measure against the layout viewport — not `window.innerHeight` —
      // because `#app-root` is sized with `100dvh`. On browsers that
      // shrink `dvh` for the soft keyboard (Chrome Android) the layout
      // viewport already collapses to the visual viewport and we must
      // not add extra padding on top, otherwise the input floats well
      // above the keyboard. On browsers that don't shrink (older iOS
      // Safari) the layout stays full-height and `gap` ends up equal
      // to the real keyboard overlap.
      const layoutH = document.documentElement?.clientHeight ?? window.innerHeight
      const gap = layoutH - vv.height - vv.offsetTop
      setOffset(gap > 1 ? gap : 0)
    }

    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  return offset
}
